import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LazyImage from '../../../components/LazyImage';
import { friendshipService } from '../../../services/socialService';
import { useFriendRequestsRealtime } from '../../../hooks/useSocialRealtime';

const FriendRequestsPanel = ({ userId, onRequestHandled }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchPendingRequests();
  }, [userId]);

  // Subscribe to real-time friendship updates
  useFriendRequestsRealtime(userId, (payload) => {
    // Refetch pending requests when friendships change
    if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
      fetchPendingRequests();
    } else if (payload?.eventType === 'DELETE') {
      // Remove from local state if deleted
      setPendingRequests(prev => prev?.filter(r => r?.id !== payload?.old?.id));
    }
  });

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const requests = await friendshipService?.getPendingRequests(userId);
      setPendingRequests(requests);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (friendshipId) => {
    setProcessingId(friendshipId);
    try {
      await friendshipService?.acceptFriendRequest(friendshipId, userId);
      setPendingRequests(prev => prev?.filter(r => r?.id !== friendshipId));
      onRequestHandled?.();
    } catch (error) {
      console.error('Error accepting friend request:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (friendshipId) => {
    setProcessingId(friendshipId);
    try {
      await friendshipService?.rejectFriendRequest(friendshipId, userId);
      setPendingRequests(prev => prev?.filter(r => r?.id !== friendshipId));
      onRequestHandled?.();
    } catch (error) {
      console.error('Error rejecting friend request:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  if (pendingRequests?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="UserCheck" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No tienes solicitudes pendientes</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
        <Icon name="UserPlus" size={20} className="text-primary" />
        <span>Solicitudes de Amistad ({pendingRequests?.length})</span>
      </h3>
      {pendingRequests?.map((request, index) => (
        <motion.div
          key={request?.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="flex items-center justify-between p-4 bg-card rounded-xl border border-border hover:border-primary/50 transition-all"
        >
          <div className="flex items-center space-x-3">
            {request?.user_profiles?.avatar_url ? (
              <LazyImage
                src={request?.user_profiles?.avatar_url}
                alt={request?.user_profiles?.full_name || 'User avatar'}
                className="w-12 h-12 rounded-full"
                sizes="48px"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-lg font-semibold text-primary">
                  {request?.user_profiles?.full_name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="font-semibold text-foreground">{request?.user_profiles?.full_name}</p>
              <p className="text-sm text-muted-foreground">
                {request?.user_profiles?.total_points} puntos
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => handleAccept(request?.id)}
              disabled={processingId === request?.id}
              loading={processingId === request?.id}
              size="sm"
              variant="default"
              iconName="Check"
            >
              Aceptar
            </Button>
            <Button
              onClick={() => handleReject(request?.id)}
              disabled={processingId === request?.id}
              size="sm"
              variant="outline"
              iconName="X"
            >
              Rechazar
            </Button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default FriendRequestsPanel;